import {
  CLOSED_ALPHA_MAP_COUNT,
  componentRarityTableForMap,
  mapCatalogEntry,
  TOTAL_MAP_COUNT,
} from '@kenocyber/shared';
import { mapRewardBpsForMap } from '../maps';
import { mobDisplaySizeForMap, mobHealthForMap } from './balance';

/**
 * **Um mapa é jogável quando tudo o que ele precisa existe** (gate V28I.BQ).
 *
 * ## O modelo falso que isto substitui
 *
 * O catálogo publicava `contentReady`, calculado por
 * `mapIndex <= CONTENT_READY_THROUGH_MAP` com a constante fixada em 1. Um
 * "pronto até N" é falso por duas razões independentes, e as duas mordiam:
 *
 * - **ele não portava nada.** Nenhuma recusa o lia e nenhum botão o consultava.
 *   `mayTravelTo` autorizava por `mapIndex <= 20` e pela linha de desbloqueio,
 *   portanto os vinte mapas já eram acessíveis enquanto a etiqueta dizia que só
 *   um estava pronto. Uma etiqueta que ninguém lê não é um portão;
 * - **ele era um número escrito à mão sobre factos que estão noutro sítio.**
 *   Quando o gate V28I.BN pôs cada mapa a desenhar a criatura dele e o V28I.BP
 *   lhe deu economia própria, o "1" não mudou sozinho — e não tinha como.
 *
 * ## A disponibilidade é uma CONJUNÇÃO de fontes canónicas
 *
 * Nada aqui é uma lista nova. Cada termo pergunta à fonte que já decide aquele
 * facto, e o predicado é a conjunção delas. Um mapa futuro torna-se jogável
 * quando as fontes passarem a cobri-lo — não quando alguém editar um segundo
 * limite.
 *
 * ## Porque ele vive AQUI e não no catálogo
 *
 * Porque `packages/shared` não consegue formar este juízo sozinho: a curva de
 * recompensa e os limites da simulação vivem em `game-domain`, que importa
 * `shared` e não o contrário. Pôr o predicado no catálogo exigiria uma
 * dependência ao contrário; pô-lo aqui é a fronteira mais baixa que consegue ver
 * todas as camadas de que ele fala.
 *
 * O catálogo continua a publicar os FACTOS — planeado, reservado, e que arte
 * pertence a cada mapa — e deixou de publicar um juízo.
 *
 * ## Jogável não é desbloqueado, e não é seleccionável
 *
 * - **jogável** é uma propriedade do MAPA: o conteúdo dele existe. É isto;
 * - **desbloqueado** é uma propriedade da CONTA: uma linha em
 *   `account_map_unlocks`, ganha ao matar o chefe do mapa anterior;
 * - **seleccionável** é as duas ao mesmo tempo, e é a única combinação que a
 *   viagem aceita.
 *
 * A autoridade continua a ser o servidor. Este predicado é puro e o cliente
 * pode calculá-lo, mas quem o faz valer é `mayTravelTo`, atrás da transacção que
 * lê a tabela de desbloqueios.
 */

/** Um termo do predicado, com o nome da fonte que o decide. */
interface PlayabilityTerm {
  readonly name: string;
  readonly holds: (mapIndex: number) => boolean;
}

/**
 * Se uma fonte que LANÇA para um índice que não cobre o aceita.
 *
 * Várias das fontes canónicas exprimem cobertura por recusa — `mobHealthForMap`
 * e `componentRarityTableForMap` lançam fora do intervalo delas. Perguntar-lhes
 * é mais honesto do que reescrever o limite aqui: se uma delas mudar de
 * intervalo, este predicado segue-a sem ser editado.
 */
function accepts(read: (mapIndex: number) => unknown): (mapIndex: number) => boolean {
  return (mapIndex) => {
    try {
      read(mapIndex);
      return true;
    } catch {
      return false;
    }
  };
}

/**
 * Os termos, e as fontes de cada um.
 *
 * A ordem não importa para o resultado; importa para a leitura, e é a do
 * enunciado: pertencer ao conjunto planeado, ter as três artes, estar coberto
 * pela economia, e caber na simulação.
 */
const PLAYABILITY_TERMS: readonly PlayabilityTerm[] = Object.freeze([
  {
    name: 'planned',
    holds: (mapIndex) => mapCatalogEntry(mapIndex).plannedForClosedAlpha,
  },
  { name: 'mob art', holds: (mapIndex) => mapCatalogEntry(mapIndex).mobAssetId !== null },
  { name: 'boss art', holds: (mapIndex) => mapCatalogEntry(mapIndex).bossAssetId !== null },
  {
    name: 'background art',
    holds: (mapIndex) => mapCatalogEntry(mapIndex).backgroundAssetId !== null,
  },
  { name: 'reward curve', holds: accepts(mapRewardBpsForMap) },
  {
    name: 'component rarity window',
    holds: accepts((mapIndex) => {
      componentRarityTableForMap(mapIndex, false);
      componentRarityTableForMap(mapIndex, true);
    }),
  },
  { name: 'mob health', holds: accepts(mobHealthForMap) },
  { name: 'mob scale', holds: accepts(mobDisplaySizeForMap) },
]);

/**
 * **Se a caça pode correr neste mapa.**
 *
 * `false` para um índice fora do catálogo, para os reservados de 21 a 50, e
 * para qualquer mapa a que falte uma das fontes. Nunca lança: um índice
 * inventado é uma pergunta legítima com uma resposta negativa, e quem chama não
 * deve ter de a distinguir de um erro.
 */
export function huntMapIsPlayable(mapIndex: number): boolean {
  if (!Number.isSafeInteger(mapIndex) || mapIndex < 1 || mapIndex > TOTAL_MAP_COUNT) {
    return false;
  }
  return PLAYABILITY_TERMS.every((term) => term.holds(mapIndex));
}

/**
 * Que termos falham num mapa, por nome. Vazio quando ele é jogável.
 *
 * Existe para o dia em que um mapa novo *quase* está pronto: a resposta a "o que
 * é que ainda falta ao mapa 21" é uma lista de nomes de fontes, e não uma
 * caçada por um booleano escondido.
 */
export function huntMapMissingCoverage(mapIndex: number): readonly string[] {
  if (!Number.isSafeInteger(mapIndex) || mapIndex < 1 || mapIndex > TOTAL_MAP_COUNT) {
    return ['outside the catalogue'];
  }
  return PLAYABILITY_TERMS.filter((term) => !term.holds(mapIndex)).map((term) => term.name);
}

/**
 * **As coberturas são provadas ao carregar**, e não numa suíte.
 *
 * A mesma disciplina do catálogo de peixes e das janelas de raridade: uma fonte
 * que deixasse de cobrir um mapa jogável tem de falhar no arranque, e não na
 * primeira viagem de um jogador.
 *
 * O que se afirma é exactamente o que o gate exigiu que fosse verificável.
 */
function assertPlayableMapsAreSound(): void {
  /* 1. Os vinte planeados são jogáveis, e sem lacunas no intervalo. */
  for (let mapIndex = 1; mapIndex <= CLOSED_ALPHA_MAP_COUNT; mapIndex += 1) {
    if (!huntMapIsPlayable(mapIndex)) {
      throw new RangeError(
        `map ${mapIndex} is planned but not playable; missing: ${huntMapMissingCoverage(mapIndex).join(', ')}`,
      );
    }
  }
  /* 2. Os reservados não são jogáveis. */
  for (let mapIndex = CLOSED_ALPHA_MAP_COUNT + 1; mapIndex <= TOTAL_MAP_COUNT; mapIndex += 1) {
    if (huntMapIsPlayable(mapIndex)) {
      throw new RangeError(
        `map ${mapIndex} is reserved for after the alpha and must not be playable`,
      );
    }
  }
  /* 3. Fora do catálogo, e valores que não são índices, respondem `false`. */
  for (const outside of [0, -1, TOTAL_MAP_COUNT + 1, 1.5, Number.NaN]) {
    if (huntMapIsPlayable(outside)) {
      throw new RangeError(`${outside} is not a map index and must not be playable`);
    }
  }
  /* 4. O conjunto jogável é um prefixo contínuo: nenhum buraco no meio. */
  let last = 0;
  for (let mapIndex = 1; mapIndex <= TOTAL_MAP_COUNT; mapIndex += 1) {
    if (!huntMapIsPlayable(mapIndex)) {
      continue;
    }
    if (mapIndex !== last + 1) {
      throw new RangeError(`the playable range has a gap before map ${mapIndex}`);
    }
    last = mapIndex;
  }
}

assertPlayableMapsAreSound();
